import { of } from 'rxjs';
import { InsightsModel } from 'src/app/common/models/common.model';

/**
 * OpenInsightsHelper
 *
 * Extracted from ChatbotexpandComponent. Holds the `openInsights` flow which streams
 * AI insights into the insights popup and records the response into chat history.
 *
 * Follows the existing `comp: any` helper pattern — the component keeps a thin wrapper
 * method (bound in the template) that delegates here, so all call sites remain unchanged
 * and behavior is preserved (verbatim `this.` -> `comp.` transcription).
 */
export class OpenInsightsHelper {
    static openInsights(comp: any, isChatResponse = false, getInsights?: any): void {
        comp.isExportspopup = false;

        if (getInsights && isChatResponse) {
            comp.insightsVal = comp.formatInsightsIntoPoints(getInsights);
        }

        if (comp.noContentFlag) {
            comp.isInsightspopupOpen = true;
            const noContentObj: InsightsModel = {
                header: comp.errorHeader || 'Analysis Status:',
                message: comp.insightsVal || 'No content, we will fix as soon as possible',
                show: true,
            };
            comp._insightsSerive.showInisghts(noContentObj);
            return;
        }

        if (comp.isInsightspopupOpen) {
            comp.isInsightspopupOpen = false;
            return;
        }

        const insightsObj: InsightsModel = {
            header: comp.failedCase ? (comp.errorHeader || 'Error:') : 'Insights',
            message: '',
            show: false,
        };
        comp._insightsSerive.showInisghts(insightsObj);

        const ShareColumn = (comp.selectedDataFormat !== 'Respondent Level')
            ? 'value'
            : (comp.isTotalBase
                ? (comp.selectedWeight === 'Unweighted' ? 'totalUnweightedShare' : 'totalWeightedShare')
                : (comp.selectedWeight === 'Unweighted' ? 'answerUnweightedShare' : 'answerWeightedShare'));

        const BaseColumn = comp.isTotalBase
            ? 'totalUnweightedBase'
            : 'answerUnweightedBase';

        comp.isInsightspopupOpen = true;
        if (!isChatResponse) {
            comp.insightsVal = [];
            comp.setFailedCaseState(false);
        }

        const initialObj: InsightsModel = {
            header: 'Insights (Processing...)',
            message: 'Analyzing data and preparing AI context...',
            show: true,
        };

        comp._insightsSerive.showInisghts(initialObj);
        let currentStreamingText = '';
        let isFirstChunk = true;

        const processingTimeout = setTimeout(() => {
            if (isFirstChunk) {
                const stillProcessingObj: InsightsModel = {
                    header: 'Insights (Still Processing...)',
                    message: 'Complex analysis in progress, please wait...',
                    show: true,
                };
                comp._insightsSerive.showInisghts(stillProcessingObj);
            }
        }, 3000);

        const updateStreamingUI = () => {
            if (currentStreamingText.trim().length === 0) {
                return;
            }

            const streamingObj: InsightsModel = {
                header: 'Insights (Streaming...)',
                message: currentStreamingText,
                show: true,
            };
            comp._insightsSerive.showInisghts(streamingObj);
            comp.insightsVal = [currentStreamingText];
        };

        setTimeout(() => {
            if (!comp.selectionSummary || comp.selectionSummary.trim() === '') {
                const rowData = comp.dataObject?.Row?.[0]?.Data?.map((d: any) => d.Child).join(', ') || 'N/A';
                const colData = comp.dataObject?.Column?.[0]?.Data?.map((d: any) => d.Child).join(', ') || 'N/A';
                const pData = comp.promptData || {};
                const mktData = pData.Markets || pData.markets || pData.Market || pData.market || [];
                const tpData = pData.Time_Period || pData.time_period || pData.TimePeriod || pData.timeperiod || [];

                const extractLabels = (item: any) => {
                    if (typeof item === 'string') {
                        const parts = item.split('|');
                        if (parts.length >= 2) {
                            return parts[parts.length - 2] + ': ' + parts[parts.length - 1];
                        }
                        return parts.pop();
                    }
                    if (item && item.name) return item.name;
                    if (item && item.Child) return item.Child;
                    return String(item);
                };

                const marketNames = mktData.length > 0 ? mktData.map((m: any) => extractLabels(m)).join(', ') : 'All Markets';
                const timePeriodNames = tpData.length > 0 ? tpData.map((t: any) => extractLabels(t)).join(', ') : 'All Time Periods';

                comp.selectionSummary = `Study: ${comp.selected_study || 'Unknown'} || Market: ${marketNames} || Time Period: ${timePeriodNames} || Row: ${rowData} || Column: ${colData}`;
            }

            let insightsDataToSend = comp.CrossTabOutPutData;
            if (comp.mcode === 2) {
                insightsDataToSend = comp.CrossTabOutPutData.map((item: any) => {
                    if (item.colChild === null || item.colChild === undefined || !item.colVariableCode) {
                        const columnData = comp.dataObject?.Column?.[0]?.Data?.[0];
                        const columnLabel = columnData?.Child || 'Total';
                        const columnVarCode = columnData?.variablecode || item.rowVariableCode || 'Total_Variable';
                        return {
                            ...item,
                            colChild: item.colChild || columnLabel,
                            colVariableCode: item.colVariableCode || columnVarCode
                        };
                    }
                    return item;
                });
            }

            const insightsObservable = getInsights && isChatResponse
                ? of(getInsights)
                : comp.crossTabService.getInsightsData(
                    insightsDataToSend,
                    comp.selected_study,
                    comp.selected_client,
                    comp.selectionSummary,
                    comp.DefaultConfidenceLevel,
                    ShareColumn,
                    BaseColumn
                );

            insightsObservable.subscribe({
                next: (chunk: string) => {
                    if (isFirstChunk) {
                        clearTimeout(processingTimeout);
                        isFirstChunk = false;
                    }

                    const chunkStr = typeof chunk === 'string' ? chunk : String(chunk);
                    const isErrorMessage = /\b(error occurred|failed|exception|invalid)\b/i.test(chunkStr);

                    if (isErrorMessage) {
                        throw new Error(chunkStr);
                    }

                    if (isChatResponse && getInsights) {
                        if (typeof chunk === 'string') {
                            currentStreamingText = chunk;
                        } else if (typeof chunk === 'object') {
                            currentStreamingText = JSON.stringify(chunk);
                        } else {
                            currentStreamingText = String(chunk);
                        }
                    } else {
                        currentStreamingText += chunk;
                    }
                    updateStreamingUI();
                },

                complete: () => {
                    clearTimeout(processingTimeout);

                    if (!currentStreamingText || currentStreamingText.trim().length === 0) {
                        comp.insightsVal = comp.fallbackMessage();
                        comp.setFailedCaseState(true);

                        if (comp.chatPreviewBlocks.length > 0) {
                            comp.updateQuestionAnswerForSelectedMessage('No data Available');
                        }

                        if (!isChatResponse) {
                            comp.pushChatResponseEntry('getInsights', 'No data Available');
                            // Removed saveChatList to prevent recording empty insights in history
                            comp.isHistorySaving = false;
                            comp.chatResponse = [];
                        }
                        const emptyObj: InsightsModel = {
                            header: comp.failedCase ? (comp.errorHeader || 'Error:') : 'Insights',
                            message: 'No data Available',
                            show: true,
                        };
                        comp._insightsSerive.showInisghts(emptyObj);
                        comp.arktabaccess = false;
                        return;
                    }

                    let processedResult = currentStreamingText;
                    try {
                        const parsed = JSON.parse(currentStreamingText);

                        if (Array.isArray(parsed)) {
                            comp.insightsVal = parsed;

                            const insightsPlainText = comp.getFirstTwoInsightsAsPlainText();
                            const hasQnAResponse = comp.chatResponse.some((entry: any) => entry.hasOwnProperty('getQnA'));
                            if (!hasQnAResponse && !isChatResponse) {
                                comp.updateQuestionAnswerForSelectedMessage(insightsPlainText || 'No data Available');
                            }

                            if (!isChatResponse) {
                                comp.pushChatResponseEntry('getInsights', currentStreamingText);
                                const qnaEntry = comp.chatResponse.find((entry: any) => entry.hasOwnProperty('getQnA'));
                                const qnaText = qnaEntry ? qnaEntry['getQnA'] : '';
                                comp.saveChatList(
                                    comp.chatPreviewBlocks.length > 0 ? comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].question : comp.inputValue,
                                    comp.chatResponse,
                                    qnaText || insightsPlainText || 'No data Available'
                                );
                                comp.chatResponse = [];
                            }

                            comp.arktabaccess = false;
                            return;
                        } else if (typeof parsed === 'object') {
                            let combinedText = '';
                            for (const key in parsed) {
                                if (parsed[key] && typeof parsed[key] === 'object' && parsed[key].Value) {
                                    combinedText += parsed[key].Value + '\n';
                                } else if (typeof parsed[key] === 'string') {
                                    combinedText += parsed[key] + '\n';
                                }
                            }
                            processedResult = combinedText;
                        } else {
                            processedResult = String(parsed);
                        }
                    } catch (e) {
                    }

                    const finalInsights = processedResult
                        .split('- ')
                        .map(item => item.trim())
                        .filter(item => item.length > 0 && !item.startsWith('```'))
                        .map(item => item.replace(/```$/, '').trim());

                    if (finalInsights.length === 0 && processedResult.trim().length > 0) {
                        const altInsights = processedResult
                            .split('\n')
                            .map(item => item.trim())
                            .filter(item => item.length > 0);

                        if (altInsights.length > 0) {
                            comp.insightsVal = altInsights;
                        } else {
                            comp.insightsVal = [processedResult.trim()];
                        }
                    } else {
                        comp.insightsVal = finalInsights;
                    }
                    const insightsPlainText = comp.getFirstTwoInsightsAsPlainText();
                    const hasQnAResponse = comp.chatResponse.some((entry: any) => entry.hasOwnProperty('getQnA'));
                    if (!hasQnAResponse && !isChatResponse) {
                        comp.updateQuestionAnswerForSelectedMessage(insightsPlainText || 'No data Available');
                    }

                    if (!isChatResponse) {
                        comp.pushChatResponseEntry('getInsights', currentStreamingText);
                        const qnaEntry = comp.chatResponse.find((entry: any) => entry.hasOwnProperty('getQnA'));
                        const qnaText = qnaEntry ? qnaEntry['getQnA'] : '';
                        comp.saveChatList(
                            comp.chatPreviewBlocks.length > 0 ? comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].question : comp.inputValue,
                            comp.chatResponse,
                            qnaText || insightsPlainText || 'No data Available'
                        );
                        comp.chatResponse = [];
                    }

                    const finalInsightsObj: InsightsModel = {
                        header: 'Insights',
                        message: currentStreamingText,
                        show: true,
                    };
                    comp._insightsSerive.showInisghts(finalInsightsObj);
                    comp.arktabaccess = false;
                },

                error: (err: any) => {
                    clearTimeout(processingTimeout);
                    console.error('An error occurred while processing insights:', err);
                    console.error('Error details:', {
                        message: err.message,
                        stack: err.stack,
                        CrossTabOutPutData: comp.CrossTabOutPutData?.length,
                        selectionSummary: comp.selectionSummary,
                        selected_study: comp.selected_study,
                        selected_client: comp.selected_client
                    });

                    const errorMessage = err.message || 'Unable to generate insights. Please check your data selection.';
                    comp.insightsVal = [errorMessage];
                    comp.setFailedCaseState(true);
                    comp.isInsightspopupOpen = true;

                    const errorObj: InsightsModel = {
                        header: 'Insights (Error)',
                        message: errorMessage,
                        show: true,
                    };
                    comp._insightsSerive.showInisghts(errorObj);
                    comp.arktabaccess = false;
                },
            });
        }, 500);
    }
}
